/////////////////////////////
// Mapping from stored data to Hierarchical tree view

import { ViewFactory, ViewProps } from "datasheets/model/ViewFactory";
import { TreeViewConfig, viewType } from "./TreeViewConfig";
import {
  Data,
  DataSet,
  ViewConfig,
  Workbook,
  isEditablePropertyType,
} from "datasheets/model/core/Workbook";
import {
  currentViewConfigAtom,
  datasetForKeyAtom,
} from "datasheets/model/atoms/WorkbookFocusAtoms";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { DataSetSelector } from "datasheets/components/selectors/DataSetSelector";
import { Button, Stack } from "@mui/material";
import { newDataset } from "datasheets/model/logic/new";
import { addNewDescriptorForColumnName } from "datasheets/model/logic/columns";
import { dataCacheAtom } from "datasheets/model/atoms/WorkbookAtoms";
import { ColumnSelector } from "datasheets/components/selectors/ColumnSelector";
import { ViewReplicator } from "datasheets/model/logic/replicate";
import {
  PersistentKeyMapper,
  dictionaryReverse,
} from "datasheets/model/utils/dictionaryUtils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Tree,
  NodeApi,
  NodeRendererProps,
  MoveHandler,
  RenameHandler,
  DeleteHandler,
  CreateHandler,
  TreeApi,
} from "react-arborist";
import { useDragDropManager } from "react-dnd";
import "./tree.view.css";
import {
  Add,
  ArrowDropDown,
  ArrowRight,
  CircleOutlined,
  FilterList,
} from "@mui/icons-material";
import { EditableTextField } from "datasheets/components/EditableTextField";
import { getOrCreateOrder } from "datasheets/model/logic/insertDeleteRows";
import {
  populateHierarchyWithNewData,
  convertHierarchyTableDataToMapOfParentsToChildren,
  convertChildMapToTreeHierarchy,
  treeViewHandleCreate,
  treeViewHandleDelete,
  treeViewHandleDrop,
  getParentFromChild,
} from "./logic/tree";
import { selectedObjectAtom } from "./selectedObjectAtom";
import { ObjectEditor } from "./ObjectEditor";
import { IdObj } from "react-arborist/dist/types/utils";
import { treeFilterAtom } from "./TreeAtoms";
import { SearchFilter, applyFilter } from "./SearchFilter";
import { FilterDialog, FilterDisplay } from "./FilterDialog";
import { addMessageAtom } from "datasheets/components/UserCommunicationsToast";

//////////////////////////////
/*
Tree View can operate in various modes:

1. Homogenous vs heterogenous
    a. Homogenous / Single table
      - all elements are rows in a single table, with a hierachy mapped between them
    b. Heterogenous / Multi table
      - different elements are represented by different tables. 
      - a further mode choice is whether each layer is a specific table (i.e. the hierarchy is fixed) or if 
        each individual element can choose its type. This adds additional complexity if we want to limit the 
        types that may be children of each parent type. If this mode is chosen, we will need to be cautious 
        on performance, to try and avoid exponential complexity due to scanning every table once for every node 
        in the hierarchy

2. Named vs automatic column
  a the parent column is specified by the user
  b the parent column is autogenerated (unless already present) - need to be careful of naming collisions

3. Owned vs referenced data
  - determines whether the view creates and owns the dataset (which would imply configuring properties after creation),
   or if dataset is referenced.
       - not owning the dataset has implications about whether we're allowed to alter it or not.
       - this may have implications on the next choice

4. Embedded hierarchy or separate hierarchy: (Not exposed to user)
  - this determines whether the hierarchy is stored on the table or stored separately.
  - storing separately has the following advantages:
    - only one table to scan to construct hierarchy, even in heterogenous mode.
    - no concerns about altering a referenced table
    - workings will not be exposed in other views of table
    - no need to propagate knowledge of columns as controlled columns to risk of end user breaking via data entry
  - storing on data table
    - can make use of data in other views
    - simpler data ownership
    - no need to have special knowledge of hierarchy table in order to exclude from selectors
    - existing columns can be used for parent relationship (although this would require implementation of a row reference in tabular view)
*/

/*
Initial implementation: (MVP)
- Referenced single table with separate hierarchy view. No control of key column
- hierarchy view has properties parent and child
*/

/////////////////////////////
// Tree View handlers and react components

function TreeController({ className }: { className?: string }) {
  const [config, setViewConfig] = useAtom(currentViewConfigAtom);
  const viewConfig = config as TreeViewConfig;
  const editMode =
    viewConfig.edit || !viewConfig.datasetId || !viewConfig.nameColumnId;
  const viewComponent = editMode ? (
    <TreeViewConfiguration config={viewConfig} setConfig={setViewConfig} />
  ) : (
    <TreeViewDisplay config={viewConfig} setConfig={setViewConfig} />
  );
  return <div className={className}>{viewComponent}</div>;
}

export type TreeItem = {
  id: string;
  name: string;
  // icon: ComponentType;
  // unread?: number;
  // readOnly: boolean;
  children?: string[];
};

function TreeViewDisplay({
  config,
  setConfig,
}: {
  config: TreeViewConfig;
  setConfig: (update: ViewConfig | ((arg: ViewConfig) => ViewConfig)) => void;
}) {
  // Should not get here if required properties are null
  if (!config.datasetId || !config.nameColumnId) {
    throw new Error("Attempt to display unconfigured TreeView");
  }
  const treeApi = useRef<TreeApi<TreeItem> | null>(null);
  const [selectedObject, setSelectedObject] = useAtom(selectedObjectAtom);
  const sourceDatasetId = config.datasetId ?? "";
  const cache = useAtomValue(dataCacheAtom);
  const [hierarchyDataset, setHierarchy] = useAtom(
    useMemo(
      () => datasetForKeyAtom(config.hierarchyDatasetId),
      [config.hierarchyDatasetId]
    )
  );
  const [sourceDataset, setSourceDataset] = useAtom(
    useMemo(() => datasetForKeyAtom(sourceDatasetId), [sourceDatasetId])
  );
  const dragDropManager = useDragDropManager();
  const sourceData = cache[sourceDatasetId];
  const properties = hierarchyDataset.properties;

  const parentKey = properties.nameToKey[parentColumnName];
  const childKey = properties.nameToKey[childColumnName];
  const nameColumnId = config.nameColumnId?.toString() ?? ""; // TODO: Allow keys to be used as index types
  // Check whether column is editable
  const nameProperty = sourceDataset.properties.descriptors[nameColumnId];
  const nameIsEditable = isEditablePropertyType(nameProperty.type);
  // Add all missing content into hierarchy as top level nodes
  const hierarchyData = populateHierarchyWithNewData(
    hierarchyDataset,
    sourceData
  );
  // Get ordering and reverse lookup
  const ordering = getOrCreateOrder(sourceDataset);
  const orderingLookup = dictionaryReverse(ordering, Number);
  // Now convert flat hierarchy to tree structure
  const nodeMap = convertHierarchyTableDataToMapOfParentsToChildren(
    hierarchyData,
    parentKey,
    childKey
  );
  const treeData = convertChildMapToTreeHierarchy(
    nodeMap,
    sourceData,
    nameColumnId
  );
  // Set up handlers
  const setSourceData = (fn: (data: Data) => Data) => {
    setSourceDataset((dataset: DataSet) => {
      const newData = fn(dataset.data);
      return { ...dataset, ...{ data: newData } };
    });
  };
  const handleNodeSelect = (nodes: NodeApi<TreeItem>[]) => {
    if (nodes.length === 1) {
      setSelectedObject(nodes[0].id);
    }
  };
  const handleRename: RenameHandler<TreeItem> = ({ id, name }) => {
    setSourceData((data: Data) => {
      const nodeObject = data[id];
      const newNodeObject = { ...nodeObject, ...{ [nameColumnId]: name } };
      return { ...data, ...{ [id]: newNodeObject } };
    });
  };
  const handleCreate: CreateHandler<TreeItem> = ({
    parentId,
    parentNode,
    index,
    type,
  }) => {
    return treeViewHandleCreate(
      nameColumnId,
      setSourceDataset,
      setHierarchy,
      childKey,
      parentKey,
      parentId,
      setSelectedObject
    );
  };
  const createFromButton = () => {
    handleCreate({
      parentId: selectedObject,
      parentNode: null,
      index: 0,
      type: "leaf",
    });
  };
  const handleDelete: DeleteHandler<TreeItem> = ({ ids }) => {
    treeViewHandleDelete(setSourceData, ids, setHierarchy, parentKey, childKey);
  };
  const handleDrop: MoveHandler<TreeItem> = ({
    dragIds,
    parentId,
    index,
    parentNode,
  }) => {
    // Get new siblings from parent node. If parentNode is null, replace with root node
    const siblings = !!parentNode
      ? parentNode.children
      : childrenAccessor(treeData["root"]);
    treeViewHandleDrop(
      index,
      siblings,
      parentId,
      orderingLookup,
      setSourceDataset,
      dragIds,
      setHierarchy,
      childKey,
      parentKey
    );
  };
  const childrenAccessor = (node: TreeItem): TreeItem[] | null => {
    const orderedChildren = node.children;
    if (!!orderedChildren) {
      orderedChildren.sort(
        (childId1, childId2) =>
          parseInt(orderingLookup[childId1]) -
          parseInt(orderingLookup[childId2])
      );
      return orderedChildren.map((childId) => treeData[childId]);
    }
    return null;
  };
  // Handle filters
  const addUserMessage = useSetAtom(addMessageAtom);
  const searchTerm: SearchFilter[] = useAtomValue(treeFilterAtom);
  const searchTermMatcher = (
    node: NodeApi<TreeItem>,
    term: string
  ): boolean => {
    const filters = JSON.parse(term) as SearchFilter[];
    const row = sourceData[node.id];
    return filters.reduce((prev, filter) => {
      return (
        prev && applyFilter(row[filter.property], filter.filter, filter.not)
      );
    }, true);
  };
  // State for filter dialog
  const [modalIsOpen, setModalIsOpen] = useState(false);

  // Add handler for key presses
  const keypressHandler = useCallback(
    (event: KeyboardEvent) => {
      // if (event.key === "Escape") {
      //   //Should cancel dialog
      // }
      if (!!treeApi.current && treeApi.current.hasFocus) {
        if (event.key === "Enter") {
          // if filter is present, block element creation
          if (searchTerm.length > 0) {
            addUserMessage("Can't create tree elements while filter is set.");
          } else {
            // Create new element
            const selectedNode = treeData[selectedObject ?? 0];
            const parentId = getParentFromChild(
              selectedNode.id,
              hierarchyData,
              parentKey,
              childKey
            );
            let index = 0;
            const nodeIdx = treeApi.current?.idToIndex[selectedNode.id] ?? 0;
            const node = treeApi.current?.at(nodeIdx);
            // Do nothing if current cell in edit mode
            if (node?.isEditing) {
              return;
            }
            const newObjectIdContainer = handleCreate({
              parentId: parentId,
              parentNode: null,
              index: index,
              type: "leaf",
            });
            if (newObjectIdContainer) {
              event.preventDefault(); //Prevent return affecting edit text
              treeApi.current?.edit(newObjectIdContainer as IdObj);
            }
          }
        }
      }
    },
    [selectedObject]
  );

  useEffect(() => {
    document.addEventListener("keydown", keypressHandler, false);

    return () => {
      document.removeEventListener("keydown", keypressHandler, false);
    };
  }, [keypressHandler]);

  // Return Component
  return (
    <div className="page">
      <div className="filterbar">
        <FilterList onClick={() => setModalIsOpen(true)} />
        <FilterDisplay columnNames={sourceDataset.properties.keyToName} />
      </div>
      <div className="mainContent">
        <div className="sidebar">
          <Tree
            ref={treeApi}
            data={childrenAccessor(treeData["root"]) ?? []}
            dndManager={dragDropManager}
            disableEdit={!nameIsEditable}
            disableDrop={false}
            onMove={handleDrop}
            onRename={handleRename}
            onCreate={handleCreate}
            onDelete={handleDelete}
            onSelect={handleNodeSelect}
            childrenAccessor={childrenAccessor}
            selection={selectedObject ?? undefined}
            width="70vw"
            searchTerm={JSON.stringify(searchTerm)}
            searchMatch={searchTermMatcher}
          >
            {Node}
          </Tree>
          <Add onClick={createFromButton} />
        </div>
        <div className="rightPanel">
          <ObjectEditor
            sourceData={sourceData}
            sourceProperties={sourceDataset.properties}
            setData={setSourceData}
          />
        </div>
      </div>
      <FilterDialog
        modalIsOpen={modalIsOpen}
        setModalIsOpen={setModalIsOpen}
        propertySet={sourceDataset.properties}
      />
    </div>
  );
}

function Node({ node, style, dragHandle }: NodeRendererProps<TreeItem>) {
  let className = "node state";
  if (node.willReceiveDrop) {
    className += " willReceiveDrop";
  }
  return (
    <div
      ref={dragHandle}
      style={style}
      className={className}
      onClick={() => node.isInternal && node.toggle()}
    >
      <FolderArrow node={node} />
      <span>
        <CircleOutlined />
      </span>
      <EditableTextField
        displayText={node.data.name}
        startInEdit={node.isEditing}
        setDisplayText={(value: string) => node.submit(value)}
        onCancel={() => node.reset()}
        onEdit={() => node.edit()}
        doubleClick={true}
        disableEdit={!node.isEditable}
        multiline={true}
      >
        {node.data.name}
      </EditableTextField>
    </div>
  );
}

function FolderArrow({ node }: { node: NodeApi<TreeItem> }) {
  if (node.isLeaf) return <span></span>;
  return <span>{node.isOpen ? <ArrowDropDown /> : <ArrowRight />}</span>;
}

function TreeViewConfiguration({
  config,
  setConfig,
}: {
  config: TreeViewConfig;
  setConfig: (update: ViewConfig | ((arg: ViewConfig) => ViewConfig)) => void;
}) {
  function selectDatasetId(datasetId: string) {
    setConfig((config: ViewConfig) => {
      return {
        ...config,
        ...{ datasetId: datasetId },
      };
    });
  }
  function selectNameColumnId(columnId: PropertyKey) {
    setConfig((config: ViewConfig) => {
      return {
        ...config,
        ...{ nameColumnId: columnId },
      };
    });
  }
  function handleSave() {
    setConfig((config: ViewConfig) => {
      return {
        ...config,
        ...{ edit: false },
      };
    });
  }
  return (
    <Stack>
      <DataSetSelector
        onSelect={selectDatasetId}
        selection={config.datasetId}
        title="Source Data Set"
      />
      <ColumnSelector
        onChange={selectNameColumnId}
        datasetId={config.datasetId}
        selection={config.nameColumnId as string}
        title="Column for Name"
        isSingleSelection={true}
      />
      <Button variant="contained" onClick={handleSave}>
        Save
      </Button>
    </Stack>
  );
}

function component(props: ViewProps) {
  return <TreeController className={props.className} />;
}

export const parentColumnName = "parent";
export const childColumnName = "child";
function generate(viewName: string, workbook: Workbook): TreeViewConfig {
  const dataset = newDataset(viewName);
  workbook.data.datasets[dataset.id] = dataset;
  // Add columns
  addNewDescriptorForColumnName(dataset.properties, parentColumnName);
  addNewDescriptorForColumnName(dataset.properties, childColumnName);
  return {
    type: viewType,
    edit: true,
    hierarchyDatasetId: dataset.id,
  };
}

const register = {
  register: function register() {
    ViewReplicator.addReplicator(
      viewType,
      (
        view: ViewConfig,
        propertyRemap: PersistentKeyMapper
      ): TreeViewConfig => {
        const treeViewConfig = view as TreeViewConfig;
        const datasetId = propertyRemap.mapNullable(treeViewConfig.datasetId);
        const hierarchyDatasetId = propertyRemap.map(
          treeViewConfig.hierarchyDatasetId
        );
        return { type: viewType, hierarchyDatasetId, datasetId };
      }
    );
    // MenuGroupFactory.addMenuGroup(
    //   viewType,
    //   <EditMenuGroup key="spreadsheet.Edit" />
    // );
    ViewFactory.addView(viewType, generate, component);
  },
};

export default register;
